import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/Status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Network, Plus, Trash2, Rocket, RefreshCw, Pencil } from "lucide-react";

export default function Mt3cx() {
  const [trunks, setTrunks] = useState([]);
  const [servers, setServers] = useState([]);
  const [form, setForm] = useState({ name: "", host: "", port: 5060, sip_server_id: "" });
  const [saving, setSaving] = useState(false);
  const [editTrunk, setEditTrunk] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [statusMap, setStatusMap] = useState({}); // {[id]: {ok, aor, contacts}}
  const [deployingId, setDeployingId] = useState(null);

  const load = () =>
    api.get("/mt-3cx").then((r) => setTrunks(r.data));

  useEffect(() => {
    load();
    api.get("/servers").then((r) => setServers(r.data));
  }, []);

  const serverName = (id) => {
    const s = servers.find((x) => x.id === id);
    return s ? s.name : id ?? "—";
  };

  const add = async () => {
    setSaving(true);
    try {
      await api.post("/mt-3cx", {
        name: form.name,
        host: form.host,
        port: Number(form.port),
        sip_server_id: form.sip_server_id,
      });
      toast.success("Trunk 3CX MT créé");
      setForm({ name: "", host: "", port: 5060, sip_server_id: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/mt-3cx/${id}`);
      toast.success("Trunk supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deploy = async (id) => {
    setDeployingId(id);
    const t = toast.loading("Déploiement en cours…");
    try {
      await api.post(`/mt-3cx/${id}/deploy`);
      toast.dismiss(t);
      toast.success("Déploiement lancé avec succès");
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeployingId(null);
    }
  };

  const fetchStatus = async (id) => {
    try {
      const { data } = await api.get(`/mt-3cx/${id}/status`);
      setStatusMap((prev) => ({ ...prev, [id]: data }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openEdit = (trunk) => {
    setEditForm({
      name: trunk.name || "",
      host: trunk.host || "",
      port: trunk.port ?? 5060,
      sip_server_id: trunk.sip_server_id ?? "",
    });
    setEditTrunk(trunk);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.put(`/mt-3cx/${editTrunk.id}`, {
        name: editForm.name,
        host: editForm.host,
        port: Number(editForm.port),
        sip_server_id: editForm.sip_server_id,
      });
      toast.success("Trunk mis à jour");
      setEditTrunk(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <div data-testid="mt3cx-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Infrastructure
          </p>
          <h1 className="mt-1 font-head text-3xl font-black tracking-tighter text-zinc-900">
            3CX Multi-tenant
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Gestion des trunks SIP 3CX partagés entre plusieurs hôtels.
          </p>
        </div>
      </div>

      {/* Formulaire création */}
      <div className="mt-6 rounded-sm border border-zinc-200 bg-white p-5">
        <p className="mb-3 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
          Nouveau trunk 3CX MT
        </p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
              Nom
            </Label>
            <Input
              data-testid="mt3cx-name-input"
              value={form.name}
              placeholder="3cx-paris"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 rounded-sm font-mono text-sm"
            />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
              Host (IP)
            </Label>
            <Input
              data-testid="mt3cx-host-input"
              value={form.host}
              placeholder="51.210.44.10"
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              className="mt-1 rounded-sm font-mono text-sm"
            />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
              Port
            </Label>
            <Input
              data-testid="mt3cx-port-input"
              type="number"
              value={form.port}
              onChange={(e) => setForm({ ...form, port: e.target.value })}
              className="mt-1 rounded-sm font-mono text-sm"
            />
          </div>
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
              Serveur SIP
            </Label>
            <select
              data-testid="mt3cx-server-select"
              value={form.sip_server_id}
              onChange={(e) => setForm({ ...form, sip_server_id: e.target.value })}
              className="mt-1 w-full rounded-sm border border-zinc-200 bg-white px-3 py-2 font-mono text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— Sélectionner —</option>
              {servers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.ip})
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            data-testid="mt3cx-add"
            onClick={add}
            disabled={saving || !form.name || !form.host || !form.sip_server_id}
            className="rounded-sm bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="mr-1 h-4 w-4" />
            {saving ? "Création…" : "Ajouter"}
          </Button>
        </div>
      </div>

      {/* Liste */}
      <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {trunks.map((t) => {
          const st = statusMap[t.id];
          return (
            <div
              key={t.id}
              data-testid={`mt3cx-row-${t.id}`}
              className="rounded-sm border border-zinc-200 bg-white p-5"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <Network className="h-4 w-4 text-zinc-400" />
                  <span className="font-head font-bold tracking-tight text-zinc-900">
                    {t.name}
                  </span>
                </div>
                <span className="inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 font-mono text-[10px] text-zinc-500">
                  {t.hotels_count ?? 0} hôtel{(t.hotels_count ?? 0) !== 1 ? "s" : ""}
                </span>
              </div>

              <dl className="mt-4 space-y-1.5 font-mono text-xs">
                <Row k="HOST" v={t.host} />
                <Row k="PORT" v={t.port} />
                <Row k="SERVEUR" v={serverName(t.sip_server_id)} />
                {t.match_ip && <Row k="MATCH IP" v={t.match_ip} />}
              </dl>

              {st && (
                <div className="mt-3 rounded-sm border border-zinc-100 bg-zinc-50 px-3 py-2 font-mono text-xs">
                  <div className="flex items-center gap-2">
                    <span
                      className={`h-2 w-2 rounded-full ${st.ok ? "bg-emerald-500" : "bg-red-400"}`}
                    />
                    <span className={st.ok ? "text-emerald-700" : "text-red-600"}>
                      {st.ok ? "Disponible" : "Indisponible"}
                    </span>
                  </div>
                  {st.aor && (
                    <div className="mt-1 text-zinc-500">
                      AOR : <span className="text-zinc-800">{st.aor}</span>
                    </div>
                  )}
                  {st.contacts != null && (
                    <div className="text-zinc-500">
                      Contacts : <span className="text-zinc-800">{st.contacts}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 flex items-center gap-0.5 border-t border-zinc-100 pt-2">
                <button
                  data-testid={`mt3cx-deploy-${t.id}`}
                  title="Déployer"
                  onClick={() => deploy(t.id)}
                  disabled={deployingId === t.id}
                  className="p-1.5 rounded-sm text-zinc-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                >
                  <Rocket className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`mt3cx-status-${t.id}`}
                  title="Statut"
                  onClick={() => fetchStatus(t.id)}
                  className="p-1.5 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`mt3cx-edit-${t.id}`}
                  title="Modifier"
                  onClick={() => openEdit(t)}
                  className="p-1.5 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  data-testid={`mt3cx-delete-${t.id}`}
                  title="Supprimer"
                  onClick={() => remove(t.id)}
                  className="ml-auto p-1.5 rounded-sm text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          );
        })}
        {trunks.length === 0 && (
          <p className="col-span-full py-8 text-center text-sm text-zinc-400">
            Aucun trunk 3CX MT configuré.
          </p>
        )}
      </div>

      {/* Dialog édition */}
      <Dialog open={!!editTrunk} onOpenChange={(o) => !o && setEditTrunk(null)}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight">
              Modifier — {editTrunk?.name}
            </DialogTitle>
          </DialogHeader>
          {editTrunk && (
            <div className="space-y-3 py-2">
              <EF
                label="Nom"
                value={editForm.name}
                onChange={(v) => setEditForm((f) => ({ ...f, name: v }))}
              />
              <EF
                label="Host (IP)"
                value={editForm.host}
                onChange={(v) => setEditForm((f) => ({ ...f, host: v }))}
                placeholder="51.210.44.10"
                mono
              />
              <EF
                label="Port"
                value={editForm.port}
                type="number"
                onChange={(v) => setEditForm((f) => ({ ...f, port: v }))}
              />
              <div>
                <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
                  Serveur SIP
                </Label>
                <select
                  value={editForm.sip_server_id}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, sip_server_id: e.target.value }))
                  }
                  className="mt-1 w-full rounded-sm border border-zinc-200 bg-white px-3 py-2 font-mono text-sm text-zinc-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">— Sélectionner —</option>
                  {servers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} ({s.ip})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditTrunk(null)}
              className="rounded-sm"
            >
              Annuler
            </Button>
            <Button
              onClick={saveEdit}
              disabled={editSaving || !editForm.name || !editForm.host}
              className="rounded-sm bg-blue-600 hover:bg-blue-700"
            >
              {editSaving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-zinc-100 pb-1 last:border-0">
      <span className="text-zinc-400">{k}</span>
      <span className="text-zinc-800">{v}</span>
    </div>
  );
}

function EF({ label, value, onChange, placeholder, type = "text", mono }) {
  return (
    <div>
      <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
        {label}
      </Label>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 rounded-sm text-sm ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}
